import { StateUpdater, useState } from 'react';
import { logInfo } from '../utils/logging';
import { deleteCookie } from '../utils/cookies';
import { ScreenType } from '../domain';
import { validateDiscountCode } from '../api/discountUtils';

type UseDiscountProps = {
  handleSubmit: (event: any) => Promise<void>;
  handleStepBack: () => void;
  handleChange: (event: any) => void;
  handleCancel: () => void;
  discountState: string;
  inputValue: string;
  isLabelShrink: boolean;
  setIsLabelShrink: StateUpdater<boolean>;
};

export function useDiscounts(
  discountCode: string,
  onDiscountCodeChanged: (code: string) => void,
  setCurrentScreen: (screen: ScreenType) => void,
): UseDiscountProps {
  const [inputValue, setInputValue] = useState('');
  const [isLabelShrink, setIsLabelShrink] = useState(!!inputValue);
  const [discountState, setDiscountState] = useState(
    discountCode ? 'success' : 'idle',
  );

  const handleCancel = async () => {
    // We are calling a not existing discount code so that
    // the discount code in the cart is cleared upon cancellation
    fetch(`/discount/giftbox-invalid-discount_G6FsDf3`, {
      credentials: 'same-origin',
      redirect: 'manual',
    });
    deleteCookie('discount_code');
    setInputValue('');
    await onDiscountCodeChanged('');
    logInfo('dispatching giftbox-discount-removed');
    document.dispatchEvent(new CustomEvent('giftbox-discount-removed'));
    setDiscountState('idle');
    setIsLabelShrink(false);
  };

  function handleChange(event) {
    setInputValue(event.target.value);
    setDiscountState('idle');
  }

  const handleStepBack = () => {
    setCurrentScreen(ScreenType.OFFERS);
    (document.activeElement as HTMLInputElement)?.blur();
  };

  const validateDiscount = async () => {
    if (inputValue === '') {
      setDiscountState('error');
      setIsLabelShrink(false);
      return;
    }
    setDiscountState('loading');
    try {
      const discountCodeValid = await validateDiscountCode(inputValue);
      if (discountCodeValid.active) {
        setInputValue('');
        fetch(`/discount/${inputValue}`, {
          credentials: 'same-origin',
          redirect: 'manual',
        });
        try {
          onDiscountCodeChanged(inputValue);
        } catch {
          setDiscountState('error');
          logInfo('failed-to-change-discount-code');
        }
        setDiscountState('success');
        setIsLabelShrink(false);
        logInfo('dispatching giftbox-discount-added');
        document.dispatchEvent(
          new CustomEvent('giftbox-discount-added', {
            detail: { discountCode: inputValue },
          }),
        );
      } else {
        setDiscountState('error');
      }
    } catch (error) {
      setDiscountState('error');
    }
  };

  function applyFnToActionableElements(cb) {
    document.querySelectorAll('.giftbox-checkout-cloned').forEach(cb);
  }

  function disableActionableElements() {
    applyFnToActionableElements((element) => {
      element.disabled = true;
      element['aria-disabled'] = 'true';
    });
  }

  function enableActionableElements() {
    applyFnToActionableElements((element) => {
      element.disabled = false;
      element['aria-disabled'] = 'false';
    });
  }

  async function handleSubmit(event) {
    event.preventDefault();
    disableActionableElements();
    await validateDiscount();
    enableActionableElements();
  }

  return {
    handleSubmit,
    handleStepBack,
    handleChange,
    handleCancel,
    discountState,
    inputValue,
    isLabelShrink,
    setIsLabelShrink,
  };
}
